import {isNil, isNotNil, range, uniq} from 'ramda';

import type {FinancialProductOfferDimensions} from '../schemas/FinancialProductOfferDimensionsSchema';
import type {FinancingCell} from '../schemas/FinancingCellSchema';

/** Down payment and balloon payment together have to stay strictly below the whole price. */
const WHOLE_PRICE_PERCENTAGE = 100;
const FALLBACK_DOWN_PAYMENT_STEP = 1;
const DOWN_PAYMENT_TICK_INTERVAL = 10;

export type FinancingControlDimensions = Pick<
  FinancialProductOfferDimensions,
  | 'availableTerms'
  | 'availableDownPaymentPercentages'
  | 'downPaymentStep'
  | 'availableBalloonPaymentPercentages'
  | 'availableFinancingCells'
>;

export interface FinancingControlSelection {
  termMonths: number | null;
  downPaymentPercentage: number | null;
  balloonPaymentPercentage: number | null;
}

export interface FinancingDownPaymentLimits {
  min: number;
  max: number;
  /** The slider's ascending labelled grid, including `min` and `max`. */
  ticks: number[];
  /** Ascending configured brackets of the selected term and balloon, at or below `max`. */
  brackets: number[];
}

export interface FinancingControlLimits {
  /** Ascending. */
  terms: number[];
  /** Ascending brackets offered on the selected term that leave a financed amount. */
  balloonPaymentPercentages: number[];
  /** Null when no down payment bracket is offered at all. */
  downPayment: FinancingDownPaymentLimits | null;
}

const ascending = (values: number[]) => uniq(values).sort((left, right) => left - right);

/** Cells on a term `availableTerms` does not list are not offerable (e.g. a non-enabled term). */
const offerableCells = (dimensions: FinancingControlDimensions) =>
  dimensions.availableFinancingCells?.filter((cell) =>
    dimensions.availableTerms.includes(cell.termMonths)
  );

const isOnCoordinate = (
  cell: FinancingCell,
  termMonths: number | null,
  balloonPaymentPercentage: number | null
) => cell.termMonths === termMonths && cell.balloonPaymentPercentage === balloonPaymentPercentage;

/** The largest value of the step grid that keeps `downPayment + balloon < 100`. */
const highestDownPaymentBelowBalloon = (balloonPaymentPercentage: number, step: number) => {
  const gridStep = step > 0 ? step : FALLBACK_DOWN_PAYMENT_STEP;

  return (
    Math.ceil((WHOLE_PRICE_PERCENTAGE - balloonPaymentPercentage) / gridStep) * gridStep - gridStep
  );
};

const toDownPaymentLimits = (
  brackets: number[],
  matrixBrackets: number[],
  balloonPaymentPercentage: number | null,
  downPaymentStep: number,
  includeGridTicks: boolean
): FinancingDownPaymentLimits | null => {
  const min = brackets.at(0);
  const matrixMax = matrixBrackets.at(-1);

  if (isNil(min) || isNil(matrixMax)) {
    return null;
  }

  // Pricing has no upper bound (a down payment above the top bracket floors onto it), so the range
  // ends at the matrix-wide maximum rather than at the selected term's highest bracket.
  const max = Math.max(
    min,
    isNil(balloonPaymentPercentage)
      ? matrixMax
      : Math.min(
          matrixMax,
          highestDownPaymentBelowBalloon(balloonPaymentPercentage, downPaymentStep)
        )
  );

  const configuredBrackets = brackets.filter((bracket) => bracket <= max);
  const gridTicks = includeGridTicks
    ? range(
        Math.floor(min / DOWN_PAYMENT_TICK_INTERVAL) + 1,
        Math.ceil(max / DOWN_PAYMENT_TICK_INTERVAL)
      ).map((index) => index * DOWN_PAYMENT_TICK_INTERVAL)
    : [];

  return {
    min,
    max,
    ticks: ascending([min, max, ...gridTicks, ...configuredBrackets]),
    brackets: configuredBrackets,
  };
};

/**
 * The limits a financing calculator offers for one selection: the terms, the balloon brackets of
 * the selected term, and the down-payment range of the selected term and balloon.
 *
 * Without `availableFinancingCells` (a backend that does not publish it yet) the union lists are
 * used, which is the previous behaviour. A term or balloon without cells also falls back to the
 * union of every bracket, so an unconstrained selection still shows a usable slider.
 */
export function getFinancingControlLimits(
  dimensions: FinancingControlDimensions,
  selection: FinancingControlSelection
): FinancingControlLimits {
  const cells = offerableCells(dimensions);
  const leavesFinancedAmount = (balloonPaymentPercentage: number) =>
    isNil(selection.downPaymentPercentage) ||
    selection.downPaymentPercentage + balloonPaymentPercentage < WHOLE_PRICE_PERCENTAGE;

  if (isNil(cells)) {
    const brackets = ascending(dimensions.availableDownPaymentPercentages);

    return {
      terms: ascending(dimensions.availableTerms),
      balloonPaymentPercentages: ascending(dimensions.availableBalloonPaymentPercentages).filter(
        leavesFinancedAmount
      ),
      downPayment: toDownPaymentLimits(
        brackets,
        brackets,
        selection.balloonPaymentPercentage,
        dimensions.downPaymentStep,
        false
      ),
    };
  }

  const matrixBrackets = ascending(cells.map((cell) => cell.downPaymentPercentage));
  const coordinateBrackets = ascending(
    cells
      .filter((cell) =>
        isOnCoordinate(cell, selection.termMonths, selection.balloonPaymentPercentage)
      )
      .map((cell) => cell.downPaymentPercentage)
  );

  return {
    terms: ascending(cells.map((cell) => cell.termMonths)),
    balloonPaymentPercentages: ascending(
      cells
        .filter((cell) => cell.termMonths === selection.termMonths)
        .map((cell) => cell.balloonPaymentPercentage)
        .filter(isNotNil)
    ).filter(leavesFinancedAmount),
    downPayment: toDownPaymentLimits(
      coordinateBrackets.length > 0 ? coordinateBrackets : matrixBrackets,
      matrixBrackets,
      selection.balloonPaymentPercentage,
      dimensions.downPaymentStep,
      true
    ),
  };
}
